'use client';
/** @jsxImportSource @emotion/react */

import React from 'react';

import type { DrawerProps as MuiDrawerProps } from '@mui/material/Drawer';

import MuiDrawer from '@mui/material/Drawer';

import { type BackdropProps } from '../backdrop/Backdrop';

type ExcludedMuiProps =
  /**
   * Removed the `component` prop from the `Drawer` component, as it should not be modified since its behaviour
   * and layout are tightly controlled by MUI, including accessibility features like focus management and
   * modal handling.
   * And the default implementation in MUI is also broken, and altering it could lead to issues.
   *
   * https://serko.atlassian.net/browse/ZDS-1032
   */
  'component';

interface DrawerProps extends Omit<MuiDrawerProps, ExcludedMuiProps> {
  /**
   * Refers to the container element where the drawer should be rendered.
   */
  containerRef?: React.RefObject<HTMLElement | null>;
}

export type { DrawerProps };

export const Drawer = React.forwardRef<HTMLDivElement, DrawerProps>(
  (
    {
      children,
      containerRef,
      ModalProps,
      PaperProps: { sx: paperPropsSx = [], ...paperProps } = {},
      SlideProps,
      variant,
      ...rest
    },
    ref
  ) => {
    const {
      slotProps: { backdrop = {}, ...slotProps } = {},
      sx: modalPropsSx = [],
      ...modalProps
    } = ModalProps || {};

    /**
     * Need to cast backdrop to include optional sx
     */
    const { sx: backdropSx = [], ...backdropProps } = backdrop as BackdropProps;

    return (
      <MuiDrawer
        {...rest}
        ref={ref}
        /**
         * If the containerRef is provided, the Drawer will be rendered as a Modal.
         **/
        ModalProps={
          containerRef
            ? {
                ...modalProps,
                container: containerRef.current,
                disableAutoFocus: true,
                disableEnforceFocus: true,
                slotProps: {
                  ...slotProps,
                  backdrop: {
                    ...backdropProps,
                    sx: [
                      ...(Array.isArray(backdropSx)
                        ? backdropSx
                        : [backdropSx]),
                      // Custom backdrop styles to prevent backdrop from covering the whole screen
                      { position: 'absolute' },
                    ],
                  },
                },
                // Custom Modal styles to prevent drawer from sliding out of the screen
                sx: [
                  ...(Array.isArray(modalPropsSx)
                    ? modalPropsSx
                    : [modalPropsSx]),
                  { position: 'absolute' },
                ],
              }
            : ModalProps
        }
        PaperProps={
          containerRef
            ? {
                ...paperProps,
                sx: [
                  ...(Array.isArray(paperPropsSx)
                    ? paperPropsSx
                    : [paperPropsSx]),
                  // Custom paper styles to prevent drawer from sliding out of the screen
                  { position: 'absolute' },
                ],
              }
            : { sx: paperPropsSx, ...paperProps }
        }
        SlideProps={
          containerRef
            ? {
                ...SlideProps,
                onExiting: (node) => {
                  if (SlideProps?.onExiting) {
                    SlideProps.onExiting(node);
                  }

                  // Custom onExiting logic to prevent drawer sliding out of the screen
                  node.style.transform = 'translateX(-100%)';
                },
              }
            : SlideProps
        }
        // If the containerRef is provided, the Drawer will be rendered as a Modal
        variant={containerRef ? 'temporary' : variant}
      >
        {children}
      </MuiDrawer>
    );
  }
);

Drawer.displayName = 'Drawer';
